import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { AuthenticatedUser } from 'app/types/index';

/**
 * Message types for cross-tab communication
 */
export type TokenUpdatedMessage = {
    type: 'token-updated';
    data: {
        token: string;
        refreshToken: string;
    };
    timestamp: number;
};

export type LogoutMessage = {
    type: 'logout';
    timestamp: number;
};

export type SyncRequestMessage = {
    type: 'sync-request';
    timestamp: number;
};

export type SyncResponseMessage = {
    type: 'sync-response';
    data: {
        token: string | null;
        refreshToken: string | null;
        user: AuthenticatedUser | null;
    };
    timestamp: number;
};

export type CrossTabMessage = TokenUpdatedMessage | LogoutMessage | SyncRequestMessage | SyncResponseMessage;

/**
 * Service for cross-tab communication using BroadcastChannel API.
 * Allows syncing authentication tokens and logout events across multiple browser tabs.
 * Requires modern browsers that support BroadcastChannel (Chrome 54+, Edge 15+, Firefox 38+, Safari 15.1+).
 */
@Injectable({
    providedIn: 'root'
})
export class CrossTabCommunicationService implements OnDestroy {
    private channel: BroadcastChannel;

    // Subjects for different message types
    private tokenUpdatedSubject = new Subject<TokenUpdatedMessage>();
    private logoutSubject = new Subject<LogoutMessage>();
    private syncRequestSubject = new Subject<SyncRequestMessage>();
    private syncResponseSubject = new Subject<SyncResponseMessage>();

    // Public observables
    public tokenUpdated$ = this.tokenUpdatedSubject.asObservable();
    public logout$ = this.logoutSubject.asObservable();
    public syncRequest$ = this.syncRequestSubject.asObservable();
    public syncResponse$ = this.syncResponseSubject.asObservable();

    public constructor(private ngZone: NgZone) {
        this.initializeCommunication();
    }

    /**
     * Initialize BroadcastChannel for cross-tab communication
     */
    private initializeCommunication(): void {
        this.ngZone.runOutsideAngular(() => {
            if (typeof BroadcastChannel === 'undefined') {
                console.warn('BroadcastChannel API not supported - cross-tab sync disabled');
                return;
            }
            this.channel = new BroadcastChannel('auth-channel');
            this.channel.onmessage = (event) => {
                this.ngZone.run(() => {
                    this.handleMessage(event.data as CrossTabMessage);
                });
            };
        });
    }

    /**
     * Handle incoming cross-tab messages via BroadcastChannel
     */
    private handleMessage(message: CrossTabMessage): void {
        switch (message.type) {
            case 'token-updated':
                this.tokenUpdatedSubject.next(message);
                break;
            case 'logout':
                this.logoutSubject.next(message);
                break;
            case 'sync-request':
                this.syncRequestSubject.next(message);
                break;
            case 'sync-response':
                this.syncResponseSubject.next(message);
                break;
        }
    }

    /**
     * Broadcast a token update to all other tabs
     */
    public broadcastTokenUpdate(token: string, refreshToken: string): void {
        const message: TokenUpdatedMessage = {
            type: 'token-updated',
            data: { token, refreshToken },
            timestamp: Date.now()
        };
        this.sendMessage(message);
    }

    /**
     * Broadcast a logout event to all other tabs
     */
    public broadcastLogout(): void {
        const message: LogoutMessage = {
            type: 'logout',
            timestamp: Date.now()
        };
        this.sendMessage(message);
    }

    /**
     * Send a sync request to other tabs (typically from a newly opened tab)
     */
    public broadcastSyncRequest(): void {
        const message: SyncRequestMessage = {
            type: 'sync-request',
            timestamp: Date.now()
        };
        this.sendMessage(message);
    }

    /**
     * Send a sync response with current authentication state
     */
    public broadcastSyncResponse(
        token: string | null,
        refreshToken: string | null,
        user: AuthenticatedUser | null = null
    ): void {
        const message: SyncResponseMessage = {
            type: 'sync-response',
            data: { token, refreshToken, user },
            timestamp: Date.now()
        };
        this.sendMessage(message);
    }

    /**
     * Send a message via BroadcastChannel
     */
    private sendMessage(message: CrossTabMessage): void {
        if (!this.channel) {
            return; // BroadcastChannel not available
        }
        this.ngZone.runOutsideAngular(() => {
            try {
                this.channel.postMessage(message);
            } catch (error) {
                console.error('Failed to send cross-tab message', error);
            }
        });
    }

    /**
     * Cleanup on service destruction
     */
    public ngOnDestroy(): void {
        if (this.channel) {
            this.channel.close();
        }
    }
}
